"use client"

import React, { useEffect, useState } from "react"
import { XMasonry, XBlock } from "react-xmasonry"
import { GameState, getIsPendingResolution } from "@azuro-org/toolkit"
import {
  type GameQuery,
  type GameMarkets,
  type Market,
} from "@azuro-org/toolkit"
import {
  useActiveMarkets,
  useBetsSummaryBySelection,
  useConditionState,
  useResolvedMarkets,
} from "@azuro-org/sdk"
import { useAccount } from "@azuro-org/sdk-social-aa-connector"
// import { useAccount } from 'wagmi'
import dayjs from "dayjs"
import cx from "classnames"

import { Tooltip } from "components/feedback"
import { Icon } from "components/ui"
import OutcomeButton from "compositions/OutcomeButton/OutcomeButton"
import EmptyContent from "compositions/EmptyContent/EmptyContent"

import OutcomeResult from "./components/ResultButton/OutcomeResult"
import Headline from "./components/Headline/Headline"
import MarketTabs from "./components/MarketTabs/MarketTabs"

import useView from "./utils/useView"
import useCollapse from "./utils/useCollapse"
import { isMarketInGroup } from "src/constants/azuroMarketGroups"

import messages from "./messages"

export const MarketsSkeleton: React.FC = () => {
  return (
    <div>
      <div className="bone rounded-full w-full h-[2.875rem]" />
      {new Array(3).fill(0).map((_, index) => (
        <div key={index}>
          <div className="flex items-center justify-between p-4">
            <div className="bone h-[1.125rem] w-24 rounded-full" />
          </div>
          <div className="space-y-2 bg-accent-grey-850 rounded-sm p-2">
            <div className="flex justify-between">
              <div className="flex gap-2 w-full">
                <div className="bone h-10 w-full rounded-min" />
                <div className="bone h-10 w-full rounded-min" />
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

type ConditionProps = {
  condition: Market["conditions"][0]
  marketName: string
  game: NonNullable<GameQuery["game"]>
  betsSummary?: Record<string, string>
  isResult?: boolean
}

const Condition: React.FC<ConditionProps> = props => {
  const { condition, marketName, game, betsSummary, isResult } = props
  const { conditionId, outcomes, state: initialState } = condition

  const { data: state, isLocked } = useConditionState({
    conditionId,
    initialState,
  })

  return (
    <div className="flex justify-between">
      <div className="flex gap-2 w-full">
        {outcomes.map(outcome => {
          const key = outcome.outcomeId

          if (isResult) {
            return (
              <OutcomeResult
                key={key}
                outcome={outcome}
                conditionState={state}
                summary={betsSummary?.[key]}
                size={40}
              />
            )
          }

          return (
            <OutcomeButton
              key={key}
              marketName={marketName}
              outcome={outcome}
              game={game}
              isLocked={isLocked}
              size={40}
            />
          )
        })}
      </div>
    </div>
  )
}

type ContentProps = {
  markets: GameMarkets
  game: NonNullable<GameQuery["game"]>
  betsSummary?: Record<string, string>
  isResult?: boolean
}

const Content: React.FC<ContentProps> = props => {
  const { markets, game, betsSummary, isResult } = props
  const [activeMarketTab, setActiveMarketTab] = useState("")

  const { areAllCollapsed, collapsedMarketIds, collapse, collapseAll } =
    useCollapse(markets)
  const { activeView, changeView } = useView()

  // Filter markets based on selected tab using market keys from markets.js
  const filteredMarkets = markets.filter(market => {
    // If no tab is selected (empty string), show all markets
    if (!activeMarketTab || activeMarketTab === "") {
      return true
    }

    // Use market key to determine if it belongs to the selected group
    return isMarketInGroup(market.marketKey, activeMarketTab)
  })

  return (
    <div className="bg-[#0F0E0D] -mx-2">
      {/* <Headline
        activeView={activeView}
        isCollapsed={areAllCollapsed}
        onChangeView={changeView}
        onCollapse={collapseAll}
      /> */}

      {/* Market Filter Tabs */}
      <div className="px-4 py-3 border-b border-accent-grey-800">
        <MarketTabs
          activeTab={activeMarketTab}
          onTabChange={setActiveMarketTab}
          availableMarkets={markets}
        />
      </div>

      <div className="">
        <XMasonry maxColumns={10} targetBlockWidth={478}>
          {filteredMarkets.map(
            ({ name, description, conditions, marketKey }) => {
              const isCollapsed = collapsedMarketIds.includes(marketKey)

              return (
                <XBlock key={name} width={activeView === "columns" ? 1 : 2}>
                  <div className="px-2">
                    <button
                      className={cx(
                        "flex items-center justify-between p-4 w-full group cursor-pointer bg-[#0F0E0D]",
                        {
                          "border-b border-b-accent-grey-1000": isCollapsed,
                        }
                      )}
                      onClick={() => collapse(marketKey)}
                    >
                      <div className="flex items-center">
                        <div className="text-caption-14 font-semibold">
                          {name}
                        </div>
                        {Boolean(description) && (
                          <Tooltip
                            text={description}
                            placement="bottom"
                            width={400}
                          >
                            <div className="w-fit ml-1 cursor-pointer text-accent-grey-200 hover:text-accent-grey-30">
                              <Icon
                                className="size-4"
                                name="interface/info-circle"
                              />
                            </div>
                          </Tooltip>
                        )}
                      </div>
                      <div className="px-2 bg-accent-grey-1000 text-accent-grey-200 group-hover:bg-accent-grey-800 group-hover:text-accent-grey-30 rounded-ssm">
                        <Icon
                          className="size-4"
                          name={
                            isCollapsed
                              ? "interface/chevron_down"
                              : "interface/chevron_up"
                          }
                        />
                      </div>
                    </button>
                    {!isCollapsed && (
                      <div className="space-y-2 bg-[#1B1A17] rounded-sm p-2">
                        {conditions.map((condition, index) => (
                          <Condition
                            key={`${index}-${condition.outcomes.length}`}
                            condition={condition}
                            marketName={name}
                            game={game}
                            betsSummary={betsSummary}
                            isResult={isResult}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </XBlock>
              )
            }
          )}
        </XMasonry>
      </div>
    </div>
  )
}

type MarketsProps = {
  game: NonNullable<GameQuery["game"]>
  gameState: GameState
}

const ResolvedMarkets: React.FC<MarketsProps> = ({ game, gameState }) => {
  const { address } = useAccount()
  const { data: markets, isLoading } = useResolvedMarkets({
    gameId: game.gameId,
  })
  const { data: betsSummary } = useBetsSummaryBySelection({
    account: address!,
    gameId: game.gameId,
    gameState,
  })

  if (isLoading) {
    return <MarketsSkeleton />
  }

  if (!markets?.length) {
    return <div>Empty</div>
  }

  return (
    <Content markets={markets} game={game} betsSummary={betsSummary} isResult />
  )
}
const WAIT_TIME = 600000

const ActiveMarkets: React.FC<MarketsProps> = ({ game, gameState }) => {
  const {
    data: markets,
    isLoading,
    isPlaceholderData,
  } = useActiveMarkets({
    gameId: game.id,
    query: {
      refetchInterval: 10_000,
    },
  })

  const isLive = gameState === GameState.Live

  const startDate = +game.startsAt * 1000
  const shouldWait = () => isLive && dayjs().diff(startDate) < WAIT_TIME
  const [waitingTime, setWaitingTime] = useState(
    shouldWait() ? WAIT_TIME - dayjs().diff(startDate) : 0
  )

  useEffect(() => {
    if (shouldWait() && !markets?.length) {
      const interval = setInterval(() => {
        const newWaitingTime = Math.max(WAIT_TIME - dayjs().diff(startDate), 0)

        if (newWaitingTime === 0) {
          clearInterval(interval)
        }
        setWaitingTime(newWaitingTime)
      }, 1000)

      return () => {
        clearInterval(interval)
      }
    }
  }, [gameState, markets])

  if (isLoading || isPlaceholderData) {
    return <MarketsSkeleton />
  }

  if (!markets) {
    if (isLive) {
      if (waitingTime) {
        const time = dayjs.duration(waitingTime).format("mm:ss")

        return (
          <EmptyContent
            image="/images/illustrations/smile_sad.png"
            title={messages.livePending.title}
            text={{ ...messages.livePending.text, values: { time } }}
          />
        )
      } else {
        return (
          <EmptyContent
            image="/images/illustrations/smile_sad.png"
            title={messages.empty.live.title}
            text={messages.empty.live.text}
          />
        )
      }
    }

    return (
      <EmptyContent
        className="py-20"
        image="/images/illustrations/smile_sad.png"
        title={messages.empty.prematch.title}
        text={messages.empty.prematch.text}
      />
    )
  }

  return <Content markets={markets} game={game} />
}

const Markets: React.FC<MarketsProps> = props => {
  const { gameState, game } = props

  if (gameState === GameState.Finished) {
    return <ResolvedMarkets {...props} />
  }
  const isPendingResolution = getIsPendingResolution({
    state: gameState,
    startsAt: game.startsAt,
  })

  if (isPendingResolution) {
    return (
      <EmptyContent
        className="py-20"
        image="/images/illustrations/smile_sad.png"
        title={messages.ended.title}
        text={messages.ended.text}
      />
    )
  }

  return <ActiveMarkets {...props} />
}

export default Markets
